/**
 * Pricing - Page publique des tarifs B8ild (Dark Theme)
 * Design cohérent avec la landing page
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";

// Composants landing page existants
import LandingHeader from "@/components/landing/LandingHeader";
import LandingFooter from "@/components/landing/LandingFooter";

// Composants pricing
import { PricingHero } from "@/components/pricing/PricingHero";
import { PricingCardsDark } from "@/components/pricing/PricingCardsDark";
import { PricingTable } from "@/components/pricing/PricingTable";
import { PricingFAQDark } from "@/components/pricing/PricingFAQDark";
import { PricingCTAFinal } from "@/components/pricing/PricingCTAFinal";
import { WaitlistModal } from "@/components/pricing/WaitlistModal";

// Hooks
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useAuth } from "@/hooks/useAuth";
import { usePageMeta } from "@/hooks/usePageMeta";
import { JsonLd } from "@/components/seo/JsonLd";

export default function Pricing() {
  usePageMeta({ title: "Tarifs B8ild — Logiciel gestion chantier BTP", description: "Découvrez les tarifs de B8ild, logiciel de suivi de rentabilité chantier BTP. Plans pour artisans et PME. Essai gratuit 14 jours sans carte bancaire.", url: "https://b8ild.fr/pricing" });

  const pricingSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "B8ild",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    offers: [
      { "@type": "Offer", name: "Starter", price: "49", priceCurrency: "EUR", url: "https://b8ild.fr/pricing", description: "1 utilisateur, projets illimités" },
      { "@type": "Offer", name: "Pro", price: "99", priceCurrency: "EUR", url: "https://b8ild.fr/pricing", description: "Jusqu'à 5 utilisateurs, fonctionnalités avancées" },
      { "@type": "Offer", name: "Entreprise", price: "149", priceCurrency: "EUR", url: "https://b8ild.fr/pricing", description: "Utilisateurs illimités, support prioritaire" },
    ],
  };

  const navigate = useNavigate();
  const { user } = useAuth();
  const { plan: currentPlan, openCheckout } = useSubscription();
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "yearly">("yearly");
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  const handleSelectPlan = async (planId: string) => {
    // Plan entreprise_plus en coming soon
    if (planId === "entreprise_plus") {
      setIsWaitlistOpen(true);
      return;
    }

    // Si l'utilisateur n'est pas connecté, rediriger vers l'inscription
    if (!user) {
      navigate(`/auth?mode=signup&plan=${planId}&period=${billingPeriod}`);
      return;
    }

    // Ouvrir le checkout Stripe
    setLoadingPlan(planId);
    try {
      await openCheckout(
        planId as "starter" | "pro" | "entreprise",
        billingPeriod
      );
    } finally {
      setLoadingPlan(null);
    }
  };

  const handleToggle = (period: "monthly" | "yearly") => {
    setBillingPeriod(period);
  };

  return (
    <TooltipProvider>
      <div className="min-h-screen bg-[#0A0A0F] overflow-x-hidden">
        <JsonLd data={pricingSchema} />
        {/* Header - même que landing page */}
        <LandingHeader />

        {/* Hero Section */}
        <PricingHero
          billingPeriod={billingPeriod}
          onToggle={handleToggle}
        />

        {/* Pricing Cards */}
        <PricingCardsDark
          billingPeriod={billingPeriod}
          onSelectPlan={handleSelectPlan}
          loadingPlan={loadingPlan}
          currentPlan={currentPlan}
        />

        {/* Comparison Table */}
        <PricingTable />

        {/* FAQ */}
        <PricingFAQDark />

        {/* CTA Final */}
        <PricingCTAFinal />

        {/* Footer - même que landing page */}
        <LandingFooter />

        {/* Modal Waitlist */}
        <WaitlistModal
          isOpen={isWaitlistOpen}
          onClose={() => setIsWaitlistOpen(false)}
        />
      </div>
    </TooltipProvider>
  );
}
